// src/pages/ContactsPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import homeStyles from '../components/home/HomeHub.module.css';
import { HomeSidebar, HomeTopBar } from '../components/home/HomeHubParts';
import NotificationsDrawer from '../components/home/NotificationsDrawer';
import GuestMergeNoticeBanner from '../components/GuestMergeNoticeBanner';
import ScheduleMeetingModal from '../components/ScheduleMeetingModal';

import styles from '../styles/contacts/ContactsWorkspace.module.css';
import ContactsToolbar from '../components/contacts/ContactsToolbar';
import ContactList, { ContactListSkeleton } from '../components/contacts/ContactList';
import ContactDetail from '../components/contacts/ContactDetail';
import ContactMenu from '../components/contacts/ContactMenu';
import ContactsEmptyState from '../components/contacts/ContactsEmptyState';
import SelectionBar from '../components/contacts/SelectionBar';
import ConfirmDialog from '../components/contacts/ConfirmDialog';
import MeetUpgradeDialog from '../components/contacts/MeetUpgradeDialog';
import { CardIncompleteDialog } from '../components/contacts/CardModals';
import AddPhysicalCardDialog from '../components/account/AddPhysicalCardDialog';
import { markPhysicalCardPromptSeen, shouldOfferPhysicalCardPrompt } from '../utils/physicalCardPrompt';
import {
  QUICK_FILTERS,
  contactDisplayName,
  filterContacts,
  groupContacts,
  quickFilterPredicate,
  sortContacts,
  summariseContact,
} from '../components/contacts/contactsData';

import api from '../utils/api';
import { trackCardAction, trackEvent } from '../utils/analytics';
import { generateVCard, shareOrDownloadVCard, vCardFilename } from '../utils/vCard';
import { useToast } from '../contexts/ToastContext';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { meetClickOutcome } from '../utils/meetAccess';
import { useContactsCached } from '../hooks/useContactsCached';
import useHubNotifications from '../hooks/useHubNotifications';

const DESKTOP_QUERY = '(min-width: 1080px)';
const PREFS_KEY = 'velox.contacts.prefs.v1';

function readPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/** Reads an offline capture queue, tolerating the legacy single-object shape. */
function readJsonArray(key) {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    localStorage.removeItem(key);
    return [];
  }
}

const ContactsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError, showInfo } = useToast();

  /* ------------------------------------------------------------- viewport */

  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(DESKTOP_QUERY).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = (event) => setIsDesktop(event.matches);
    // Safari < 14 only has the deprecated listener API.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  /* ----------------------------------------------------------------- data */

  const [userData, setUserData] = useState(null);
  const { contacts, isLoading, error: contactsError, addContact, refreshContacts } = useContactsCached(
    userData?._id
  );
  const [removedContactIds, setRemovedContactIds] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    api
      .get('/api/user/account')
      .then((response) => {
        if (!cancelled) setUserData(response.data);
      })
      .catch((err) => console.error('Error fetching user data:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  /* ------------------------------------------------------- view + filters */

  const storedPrefs = useRef(readPrefs());
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState(() => storedPrefs.current.sort || 'recent');
  const [group, setGroup] = useState(() => storedPrefs.current.group || 'date');
  const [source, setSource] = useState('any');
  const [view, setView] = useState(() => storedPrefs.current.view || 'rows');

  useEffect(() => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify({ sort, group, view }));
    } catch {
      /* preferences are a nicety; ignore quota or private-mode failures */
    }
  }, [sort, group, view]);

  // The flagged filter stays in the URL so /contacts?filter=flagged (linked
  // from the Home KPI card) keeps working and stays shareable.
  const urlFilter = (searchParams.get('filter') || '').toLowerCase();
  const quickFilter = QUICK_FILTERS.some((f) => f.id === urlFilter) ? urlFilter : 'all';

  const setQuickFilter = useCallback(
    (next) => {
      const params = new URLSearchParams(searchParams);
      if (next && next !== 'all') params.set('filter', next);
      else params.delete('filter');
      setSearchParams(params, { replace: true });
      trackEvent('Filter', 'Contacts Quick Filter', next);
    },
    [searchParams, setSearchParams]
  );

  /* --------------------------------------------------------- the pipeline */

  // One clock for every relative timestamp on the page, refreshed each minute
  // so "2m ago" does not go stale while the tab sits open.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const liveContacts = useMemo(() => {
    const list = Array.isArray(contacts) ? contacts : [];
    if (removedContactIds.size === 0) return list;
    return list.filter((contact) => contact?._id && !removedContactIds.has(contact._id));
  }, [contacts, removedContactIds]);

  const visibleContacts = useMemo(
    () => sortContacts(filterContacts(liveContacts, { query, quickFilter, source }), sort),
    [liveContacts, query, quickFilter, source, sort]
  );

  const groups = useMemo(() => groupContacts(visibleContacts, group, now), [visibleContacts, group, now]);

  const filterCounts = useMemo(() => {
    const counts = {};
    QUICK_FILTERS.forEach((filter) => {
      counts[filter.id] =
        filter.id === 'all' ? liveContacts.length : liveContacts.filter(quickFilterPredicate(filter.id)).length;
    });
    return counts;
  }, [liveContacts]);

  const isFiltered = Boolean(query.trim()) || quickFilter !== 'all' || source !== 'any';

  // Debounced so a search reports one event rather than one per keystroke.
  useEffect(() => {
    const term = query.trim();
    if (!term) return undefined;
    const timer = setTimeout(() => trackEvent('Search', 'Contacts Query', term), 800);
    return () => clearTimeout(timer);
  }, [query]);

  const resetFilters = useCallback(() => {
    setQuery('');
    setSource('any');
    setQuickFilter('all');
  }, [setQuickFilter]);

  /* ------------------------------------------------------------ selection */

  const [selectedId, setSelectedId] = useState(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [openMenuId, setOpenMenuId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const selectedContact = useMemo(
    () => visibleContacts.find((contact) => contact?._id === selectedId) || null,
    [visibleContacts, selectedId]
  );

  // Keep a selection on desktop, where the panel is always on screen. On
  // touch the sheet is opened deliberately, so nothing is auto-selected.
  useEffect(() => {
    if (visibleContacts.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    const stillVisible = selectedId && visibleContacts.some((contact) => contact._id === selectedId);
    if (stillVisible) return;
    if (isDesktop) setSelectedId(visibleContacts[0]._id);
    else if (selectedId) {
      setSelectedId(null);
      setIsSheetOpen(false);
    }
  }, [visibleContacts, selectedId, isDesktop]);

  // Checked rows that scroll out of the filter must not stay in a bulk action.
  useEffect(() => {
    setCheckedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(visibleContacts.map((contact) => contact._id));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [visibleContacts]);

  const checkedContacts = useMemo(
    () => visibleContacts.filter((contact) => checkedIds.has(contact._id)),
    [visibleContacts, checkedIds]
  );

  const toggleCheck = useCallback((id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const activateContact = useCallback(
    (contact) => {
      if (!contact?._id) return;
      setSelectedId(contact._id);
      if (!isDesktop) setIsSheetOpen(true);
      trackCardAction('select_contact', contact._id);
    },
    [isDesktop]
  );

  const closeSheet = useCallback(() => setIsSheetOpen(false), []);

  /* ----------------------------------------------------- contact mutations */

  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false);
  const [pendingRetract, setPendingRetract] = useState(null);
  const [cardBlockReason, setCardBlockReason] = useState(null);
  const [meetingContact, setMeetingContact] = useState(null);
  const [meetGateOpen, setMeetGateOpen] = useState(false);
  const { isPro, isLoading: entitlementsLoading } = useEntitlements();

  const openMeet = useCallback(
    (contact) => {
      const outcome = meetClickOutcome({ isLoading: entitlementsLoading, isPro });
      if (outcome === 'ignore') return;
      if (outcome === 'gate') {
        setMeetGateOpen(true);
        return;
      }
      setMeetingContact(contact);
    },
    [entitlementsLoading, isPro]
  );

  const toggleFlag = useCallback(
    async (contact) => {
      if (!contact?._id) return;
      const wasFlagged = Boolean(contact.isFlagged);
      // Optimistic: the cache is the render source, so write through it.
      addContact({ ...contact, isFlagged: !wasFlagged });
      try {
        const response = await api.patch(`/api/contact/${contact._id}/toggle-flag`);
        addContact({ ...contact, isFlagged: response.data.isFlagged });
        trackCardAction(wasFlagged ? 'unflag_contact' : 'flag_contact', contact._id);
      } catch (err) {
        addContact({ ...contact, isFlagged: wasFlagged });
        showError('Could not update the flag. Please try again.');
      }
    },
    [addContact, showError]
  );

  const hideContact = useCallback((id) => {
    setRemovedContactIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const unhideContact = useCallback((id) => {
    setRemovedContactIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const deleteContact = useCallback(
    async (contact) => {
      if (!contact?._id) return false;
      hideContact(contact._id);
      try {
        await api.delete(`/api/contact/${contact._id}`);
        trackCardAction('delete_contact', contact._id);
        showSuccess(`${contactDisplayName(contact)} removed`);
        await refreshContacts?.();
        return true;
      } catch (err) {
        unhideContact(contact._id);
        console.error('Error deleting contact:', err);
        showError('Could not remove this contact. Please try again.');
        return false;
      }
    },
    [hideContact, unhideContact, refreshContacts, showError, showSuccess]
  );

  const saveContactDetails = useCallback(
    async (contact, changes) => {
      try {
        const response = await api.put(`/api/contact/${contact._id}`, changes);
        if (response?.data?._id) {
          // Preserve the populated `contact` sub-document: the update endpoint
          // returns the raw record without it.
          addContact({ ...contact, ...response.data, contact: contact.contact });
        } else {
          addContact({ ...contact, ...changes });
        }
        trackCardAction('edit_contact', contact._id);
        showSuccess('Contact updated');
        return true;
      } catch (err) {
        const message = err?.response?.data?.error || 'Could not save these changes. Please try again.';
        showError(message);
        return false;
      }
    },
    [addContact, showError, showSuccess]
  );

  const saveNotes = useCallback(
    async (contactId, text) => {
      const response = await api.put(`/api/contact/${contactId}`, { notes: text });
      if (response?.data?._id) {
        const existing = (Array.isArray(contacts) ? contacts : []).find((c) => c._id === contactId);
        addContact({ ...(existing || {}), ...response.data, contact: existing?.contact ?? response.data.contact });
      }
      return response?.data;
    },
    [addContact, contacts]
  );

  /* --------------------------------------------------------- card actions */

  const cardReadiness = useMemo(() => {
    if (!userData) return { ready: false, reason: null };
    const card = userData.businessCard || {};
    if (!card.name || !card.email) return { ready: false, reason: 'incomplete' };
    return { ready: true, reason: null };
  }, [userData]);

  const [sendingCard, setSendingCard] = useState(false);

  const sendMyCard = useCallback(
    async (contact) => {
      if (!contact?._id || sendingCard) return;
      if (!cardReadiness.ready) {
        setCardBlockReason(cardReadiness.reason || 'no-card');
        return;
      }
      setSendingCard(true);
      try {
        const response = await api.post(`/api/contact/send-card/${contact._id}`);
        addContact({ ...contact, contactDetailsSent: true, inviteSentByEmail: true });
        trackCardAction('send_card', contact._id);
        showSuccess(response?.data?.message || 'Your card has been sent');
      } catch (err) {
        console.error('Failed to send business card:', err);
        showError('Could not send your card. Please try again later.');
        trackEvent('Error', 'Send Card Failed', err?.message || 'unknown');
      } finally {
        setSendingCard(false);
      }
    },
    [addContact, cardReadiness, sendingCard, showError, showSuccess]
  );

  // Retracting deletes the recipient's whole copy of your card, so it is
  // confirmed rather than fired straight from the menu.
  const requestRetract = useCallback(
    (contact) => {
      const contactUserId = contact?.contact?._id || contact?.contact;
      if (!contactUserId) {
        showError('This contact cannot be retracted.');
        return;
      }
      setPendingRetract(contact);
    },
    [showError]
  );

  const retractMyCard = useCallback(
    async (contact) => {
      const contactUserId = contact?.contact?._id || contact?.contact;
      if (!contactUserId) {
        showError('This contact cannot be retracted.');
        return;
      }
      try {
        await api.delete(`/api/contact/retract-card/${contactUserId}`);
        addContact({
          ...contact,
          contactDetailsSent: false,
          inviteSentByEmail: false,
          theyHaveMyCard: false,
        });
        trackCardAction('retract_card', contact._id);
        showSuccess('Your card was retracted');
      } catch (err) {
        showError(err?.response?.data?.error || 'Could not retract your card.');
      }
    },
    [addContact, showError, showSuccess]
  );

  const saveVcf = useCallback(
    (contact) => {
      shareOrDownloadVCard(contact, { showSuccess, showError }, { trackId: contact?._id });
    },
    [showError, showSuccess]
  );

  const shareContact = useCallback(
    async (contact) => {
      if (!contact) return;
      const text = summariseContact(contact);
      try {
        if (navigator.share) {
          await navigator.share({ title: contactDisplayName(contact), text });
        } else if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
          showSuccess('Contact details copied to clipboard');
        } else {
          showInfo('Sharing is not supported on this device');
          return;
        }
        trackCardAction('share_contact', contact._id);
      } catch (err) {
        if (err?.name !== 'AbortError') showError('Could not share this contact.');
      }
    },
    [showError, showInfo, showSuccess]
  );

  const copyEmail = useCallback(
    async (contact) => {
      if (!contact?.email) return;
      try {
        await navigator.clipboard.writeText(contact.email);
        showSuccess('Email address copied');
      } catch {
        showError('Could not copy the email address.');
      }
    },
    [showError, showSuccess]
  );

  /* ---------------------------------------------------------- bulk actions */

  const bulkFlag = useCallback(async () => {
    if (checkedContacts.length === 0) return;
    const allFlagged = checkedContacts.every((contact) => contact.isFlagged);
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(
        checkedContacts
          .filter((contact) => Boolean(contact.isFlagged) === allFlagged)
          .map(async (contact) => {
            const response = await api.patch(`/api/contact/${contact._id}/toggle-flag`);
            addContact({ ...contact, isFlagged: response.data.isFlagged });
          })
      );
      const failed = results.filter((result) => result.status === 'rejected').length;
      if (failed > 0) showError(`${failed} contact${failed > 1 ? 's' : ''} could not be updated.`);
      else showSuccess(allFlagged ? 'Flags removed' : 'Contacts flagged');
    } finally {
      setBulkBusy(false);
    }
  }, [addContact, checkedContacts, showError, showSuccess]);

  const bulkExport = useCallback(() => {
    if (checkedContacts.length === 0) return;
    // One .vcf holding a VCARD block per contact; every importer accepts this.
    const payload = checkedContacts.map((contact) => generateVCard(contact)).join('');
    const blob = new Blob([payload], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download =
      checkedContacts.length === 1
        ? `${vCardFilename(checkedContacts[0].name)}.vcf`
        : `velox-contacts-${checkedContacts.length}.vcf`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      if (document.body.contains(link)) document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 1000);
    trackEvent('Export', 'Contacts vCard', String(checkedContacts.length));
    showSuccess(`${checkedContacts.length} contact${checkedContacts.length > 1 ? 's' : ''} exported`);
  }, [checkedContacts, showSuccess]);

  const bulkDelete = useCallback(async () => {
    const targets = checkedContacts;
    if (targets.length === 0) return;
    setBulkBusy(true);
    try {
      targets.forEach((contact) => hideContact(contact._id));
      const results = await Promise.allSettled(
        targets.map((contact) => api.delete(`/api/contact/${contact._id}`))
      );
      const failures = [];
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          failures.push(targets[index]);
          unhideContact(targets[index]._id);
        }
      });
      const removed = targets.length - failures.length;
      if (removed > 0) showSuccess(`${removed} contact${removed > 1 ? 's' : ''} removed`);
      if (failures.length > 0) showError(`${failures.length} could not be removed.`);
      setCheckedIds(new Set());
      setPendingBulkDelete(false);
      await refreshContacts?.();
    } finally {
      setBulkBusy(false);
    }
  }, [checkedContacts, hideContact, unhideContact, refreshContacts, showError, showSuccess]);

  /* ----------------------------------------------------- card activation */

  const [showActivation, setShowActivation] = useState(false);

  useEffect(() => {
    const userId = userData?._id;
    if (!shouldOfferPhysicalCardPrompt(userId, userData?.physicalCards)) {
      setShowActivation(false);
      return undefined;
    }
    setShowActivation(true);
    const timer = window.setTimeout(() => markPhysicalCardPromptSeen(userId), 0);
    return () => window.clearTimeout(timer);
  }, [userData]);

  const dismissActivation = useCallback(() => {
    if (userData?._id) markPhysicalCardPromptSeen(userData._id);
    setShowActivation(false);
  }, [userData?._id]);

  /* ----------------------------------------------------------- home chrome */

  const [isNavOpen, setIsNavOpen] = useState(false);
  const notifications = useHubNotifications({
    onInvitationAccepted: () => refreshContacts?.(),
  });

  /* ------------------------------------------------ offline capture queues */

  const processedPendingRef = useRef(false);
  const processedQueuedRef = useRef(false);

  /**
   * Cards tapped while signed out are parked in localStorage by the public
   * card page. Contacts is where they get turned into real records.
   */
  const drainPendingCards = useCallback(async () => {
    if (processedPendingRef.current) return;
    if (!localStorage.getItem('token')) return;
    if (!localStorage.getItem('pendingCardSave')) return;
    processedPendingRef.current = true;

    try {
      const cards = readJsonArray('pendingCardSave');
      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      const fresh = cards.filter((card) => (typeof card?.timestamp === 'number' ? card.timestamp : 0) > cutoff);
      const expired = cards.length - fresh.length;

      if (expired > 0) {
        showInfo(
          `${expired} card${expired > 1 ? 's' : ''} expired (older than 24 hours). Please tap again to save.`
        );
      }
      if (fresh.length === 0) {
        localStorage.removeItem('pendingCardSave');
        return;
      }

      const account = await api.get('/api/user/account');
      let saved = 0;
      let skipped = 0;
      const failed = [];

      for (const card of fresh) {
        try {
          // Sequential on purpose: these are duplicate-sensitive writes and
          // the list is at most a handful of cards.
          // eslint-disable-next-line no-await-in-loop
          const ref = String(card.nfcUID || '').trim();
          const ownerPath = /^[a-fA-F0-9]{24}$/.test(ref)
            ? `/api/c/${ref}`
            : `/api/nfc/${encodeURIComponent(ref)}`;
          const owner = await api.get(ownerPath);
          if (owner.data.cardInfo?.userId === account.data._id) {
            skipped += 1;
            continue;
          }
          // eslint-disable-next-line no-await-in-loop
          const result = await api.post('/api/contact/create', {
            nfcUID: card.nfcUID,
            name: card.name,
            email: card.email,
            phone: card.phone,
            company: card.company,
            role: card.role,
            linkedinURL: card.linkedinURL,
            additionalURL: card.additionalURL,
            title: card.title,
          });
          if (result.status === 201) saved += 1;
        } catch (saveError) {
          const isDuplicate =
            saveError.response?.status === 400 &&
            typeof saveError.response?.data?.error === 'string' &&
            saveError.response.data.error.includes('Contact already exists');
          if (isDuplicate) skipped += 1;
          else failed.push(card);
        }
      }

      if (saved > 0) {
        showSuccess(`${saved} contact${saved > 1 ? 's' : ''} saved`);
        await refreshContacts?.();
      }
      if (skipped > 0) {
        showInfo(`${skipped} contact${skipped > 1 ? 's' : ''} skipped (already saved or your own card).`);
      }
      if (failed.length > 0) {
        localStorage.setItem('pendingCardSave', JSON.stringify(failed));
        showError(
          `Could not save ${failed.length} contact${failed.length > 1 ? 's' : ''}. We will retry next time you open Contacts.`
        );
      } else {
        localStorage.removeItem('pendingCardSave');
      }
    } catch (err) {
      console.error('Error processing pending card saves:', err);
    }
  }, [refreshContacts, showError, showInfo, showSuccess]);

  /** Cards tapped while signed in but offline. */
  const drainQueuedContacts = useCallback(async () => {
    if (processedQueuedRef.current) return;
    if (!localStorage.getItem('token') || !navigator.onLine) return;

    const queued = readJsonArray('queuedContacts').filter(Boolean);
    if (queued.length === 0) return;
    processedQueuedRef.current = true;

    showInfo(`Processing ${queued.length} queued contact${queued.length > 1 ? 's' : ''}…`);
    let success = 0;
    let failures = 0;

    for (const entry of queued) {
      // The queued payload carries a stale Authorization header; api adds a
      // current one.
      const { headers, ...payload } = entry;
      try {
        // eslint-disable-next-line no-await-in-loop
        await api.post('/api/contact/create', payload);
        success += 1;
      } catch (err) {
        const isDuplicate =
          err.response?.status === 400 &&
          typeof err.response?.data?.error === 'string' &&
          err.response.data.error.includes('Contact already exists');
        if (isDuplicate) success += 1;
        else failures += 1;
      }
    }

    localStorage.removeItem('queuedContacts');
    if (success > 0) {
      showSuccess(`${success} queued contact${success > 1 ? 's' : ''} saved`);
      await refreshContacts?.();
    }
    if (failures > 0) showError(`Failed to save ${failures} queued contact${failures > 1 ? 's' : ''}.`);
  }, [refreshContacts, showError, showInfo, showSuccess]);

  useEffect(() => {
    // Let the first paint land before touching the network.
    const timer = setTimeout(() => {
      drainPendingCards();
      drainQueuedContacts();
    }, 500);
    return () => clearTimeout(timer);
  }, [drainPendingCards, drainQueuedContacts]);

  useEffect(() => {
    const onOnline = () => {
      if (readJsonArray('queuedContacts').filter(Boolean).length === 0) return;
      processedQueuedRef.current = false;
      drainQueuedContacts();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [drainQueuedContacts]);

  // `/edit-contacts` set this flag for the old page's edit mode; editing is
  // now always available on the selected contact, so just clear it.
  useEffect(() => {
    localStorage.removeItem('enableEditMode');
  }, []);

  /* --------------------------------------------------- sticky measurements */

  const workspaceRef = useRef(null);
  const toolbarRef = useRef(null);

  /*
   * The sticky group headers and the detail panel all park below the command
   * bar, so they need its real height. That height is not a constant: the
   * controls wrap at intermediate widths, and on mobile the filter panel
   * expands and collapses. Measuring it beats maintaining a magic number that
   * silently drifts and leaves those surfaces tucked behind the bar.
   */
  useEffect(() => {
    const toolbar = toolbarRef.current;
    const workspace = workspaceRef.current;
    if (!toolbar || !workspace || typeof ResizeObserver === 'undefined') return undefined;

    const publish = () => {
      const { height } = toolbar.getBoundingClientRect();
      if (height > 0) workspace.style.setProperty('--c-toolbar', `${Math.round(height)}px`);
    };

    const observer = new ResizeObserver(publish);
    observer.observe(toolbar);
    return () => observer.disconnect();
  }, []);

  /* ------------------------------------------------------------- shortcuts */

  const searchInputRef = useRef(null);

  useEffect(() => {
    trackEvent('Page View', 'Contacts Page', 'User viewed contacts list');
  }, []);

  const anyOverlayOpen =
    isNavOpen ||
    notifications.open ||
    showActivation ||
    Boolean(cardBlockReason) ||
    Boolean(pendingDelete) ||
    pendingBulkDelete ||
    Boolean(pendingRetract) ||
    Boolean(meetingContact) ||
    meetGateOpen ||
    (!isDesktop && isSheetOpen);

  useEffect(() => {
    const onKeyDown = (event) => {
      // A dialog or sheet owns the keyboard while it is open, including its own
      // Escape handling.
      if (anyOverlayOpen) return;

      const target = event.target;
      const isTyping =
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable);

      if ((event.key === 'k' || event.key === 'K') && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
        return;
      }
      if (isTyping) {
        if (event.key === 'Escape' && target === searchInputRef.current) {
          setQuery('');
          target.blur();
        }
        return;
      }
      if (event.key === '/') {
        event.preventDefault();
        searchInputRef.current?.focus();
        return;
      }
      if (event.key === 'Escape') {
        if (checkedIds.size > 0) setCheckedIds(new Set());
        else if (isFiltered) resetFilters();
        return;
      }
      if (event.key === 'f' && selectedContact && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        toggleFlag(selectedContact);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [anyOverlayOpen, checkedIds.size, isFiltered, resetFilters, selectedContact, toggleFlag]);

  // hubRoot owns the page scroll, so overlays lock the document instead.
  useEffect(() => {
    if (!anyOverlayOpen) return undefined;
    const previousBody = document.body.style.overflow;
    const previousHtml = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousBody;
      document.documentElement.style.overflow = previousHtml;
    };
  }, [anyOverlayOpen]);

  /* ------------------------------------------------------------ menu wiring */

  const menuActions = useMemo(
    () => ({
      onOpen: (contact) => {
        setSelectedId(contact._id);
        if (!isDesktop) setIsSheetOpen(true);
      },
      onEmail: (contact) => trackCardAction('email_click', contact._id),
      onCall: (contact) => trackCardAction('phone_click', contact._id),
      onCopyEmail: copyEmail,
      onSaveVcf: saveVcf,
      onShare: shareContact,
      onToggleFlag: toggleFlag,
      onSendCard: sendMyCard,
      onRetractCard: requestRetract,
      onDelete: (contact) => setPendingDelete(contact),
    }),
    [copyEmail, isDesktop, requestRetract, saveVcf, sendMyCard, shareContact, toggleFlag]
  );

  const menuContact = useMemo(
    () => (Array.isArray(contacts) ? contacts.find((contact) => contact?._id === openMenuId) : null) || null,
    [contacts, openMenuId]
  );

  /* -------------------------------------------------------------- rendering */

  const detailProps = {
    contact: selectedContact,
    isDesktop,
    now,
    onClose: closeSheet,
    onToggleFlag: toggleFlag,
    onSaveDetails: saveContactDetails,
    onSaveNotes: saveNotes,
    onDelete: (contact) => setPendingDelete(contact),
    onSendCard: sendMyCard,
    onRetractCard: requestRetract,
    onSaveVcf: saveVcf,
    onShare: shareContact,
    onScheduleMeeting: openMeet,
    onOpenFullMap: () => navigate('/map'),
    onTrackLink: (action) => selectedContact && trackCardAction(action, selectedContact._id),
  };

  const totalContacts = liveContacts.length;

  let listBody;
  if (isLoading && totalContacts === 0) {
    listBody = <ContactListSkeleton />;
  } else if (contactsError && totalContacts === 0) {
    listBody = (
      <ContactsEmptyState
        variant="error"
        title="We could not load your contacts"
        body="The connection to Velox failed. Your saved contacts are safe — try loading them again."
        actions={[{ label: 'Try again', primary: true, onClick: () => refreshContacts?.() }]}
      />
    );
  } else if (totalContacts === 0) {
    listBody = (
      <ContactsEmptyState
        title="No contacts yet"
        body="Contacts appear here when you save someone's Velox card, or when you accept an invitation. Only live Velox cards are stored, so every contact stays up to date on its own."
        actions={[{ label: 'Open my card', primary: true, onClick: () => navigate('/my-card') }]}
      />
    );
  } else if (visibleContacts.length === 0) {
    listBody = (
      <ContactsEmptyState
        variant="search"
        title={query.trim() ? `Nothing matches “${query.trim()}”` : 'Nothing matches these filters'}
        body="Try a different spelling, or clear the search and filters to see your whole network."
        actions={[{ label: 'Reset filters', primary: false, onClick: resetFilters }]}
      />
    );
  } else {
    listBody = (
      <ContactList
        groups={groups}
        view={view}
        selectedId={selectedId}
        checkedIds={checkedIds}
        openMenuId={isDesktop ? openMenuId : null}
        menuVariant="popover"
        onSelect={setSelectedId}
        onActivate={activateContact}
        onToggleCheck={toggleCheck}
        onToggleFlag={toggleFlag}
        onOpenMenu={setOpenMenuId}
        onCloseMenu={() => setOpenMenuId(null)}
        menuActions={menuActions}
        now={now}
      />
    );
  }

  return (
    // hubRoot is the page's scroll container, so an open overlay has to clamp
    // it here; the document-level lock above only covers html/body.
    <div className={homeStyles.hubRoot} style={anyOverlayOpen ? { overflow: 'hidden' } : undefined}>
      <HomeSidebar
        open={isNavOpen}
        onClose={() => setIsNavOpen(false)}
        activePath={location.pathname}
        onNavigate={(to) => {
          setIsNavOpen(false);
          navigate(to);
        }}
      />

      <NotificationsDrawer
        open={notifications.open}
        onClose={notifications.close}
        {...notifications.drawerProps}
      />

      <div className={homeStyles.hubMain}>
        <HomeTopBar
          title="Contacts"
          onMenu={() => setIsNavOpen(true)}
          notificationsBadge={notifications.badge}
          onNotifications={notifications.toggle}
        />

        <div className={homeStyles.hubContent}>
          <GuestMergeNoticeBanner />

          <div
            ref={workspaceRef}
            className={`${styles.workspace} ${checkedIds.size > 0 ? styles.workspaceSelecting : ''}`}
          >
            <header className={styles.pageHead}>
              <div className={styles.pageHeadText}>
                <p className={styles.eyebrow}>Your network</p>
                <h1 className={styles.pageTitle}>Contacts</h1>
              </div>
            </header>

            <ContactsToolbar
              containerRef={toolbarRef}
              searchInputRef={searchInputRef}
              query={query}
              onQueryChange={setQuery}
              quickFilter={quickFilter}
              onQuickFilterChange={setQuickFilter}
              filterCounts={filterCounts}
              source={source}
              onSourceChange={setSource}
              sort={sort}
              onSortChange={setSort}
              group={group}
              onGroupChange={setGroup}
              view={view}
              onViewChange={setView}
              shownCount={visibleContacts.length}
              totalCount={totalContacts}
              isFiltered={isFiltered}
              isLoading={isLoading && totalContacts === 0}
              onReset={resetFilters}
            />

            <div className={styles.panes}>
              <div className={styles.listPane}>{listBody}</div>
              {/* Only mounted on the wide layout: below 1080px the profile
                  arrives as the sheet below, and mounting both would run two
                  notes autosaves and two maps for the same contact. */}
              {isDesktop ? (
                <aside className={styles.detailPane} aria-label="Contact details">
                  <ContactDetail {...detailProps} />
                </aside>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Touch layout: the profile arrives as a sheet over the list. */}
      {!isDesktop && isSheetOpen && selectedContact ? (
        <>
          <div className={styles.sheetOverlay} onClick={closeSheet} aria-hidden="true" />
          <div
            className={styles.sheet}
            role="dialog"
            aria-modal="true"
            aria-label={`${contactDisplayName(selectedContact)} details`}
          >
            <div className={styles.sheetBody}>
              <ContactDetail {...detailProps} />
            </div>
          </div>
        </>
      ) : null}

      {/* Touch layout: long press and the row overflow button open a sheet. */}
      {!isDesktop && menuContact ? (
        <ContactMenu
          contact={menuContact}
          variant="sheet"
          onClose={() => setOpenMenuId(null)}
          actions={menuActions}
        />
      ) : null}

      <SelectionBar
        count={checkedIds.size}
        allFlagged={checkedContacts.length > 0 && checkedContacts.every((contact) => contact.isFlagged)}
        busy={bulkBusy}
        onFlag={bulkFlag}
        onExport={bulkExport}
        onDelete={() => setPendingBulkDelete(true)}
        onClear={() => setCheckedIds(new Set())}
      />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Remove this contact?"
        lead={
          pendingDelete
            ? `${contactDisplayName(pendingDelete)} will be removed from your contacts, along with any notes you saved. This cannot be undone.`
            : ''
        }
        confirmLabel="Remove contact"
        onClose={() => setPendingDelete(null)}
        onConfirm={async () => {
          const target = pendingDelete;
          setPendingDelete(null);
          if (target) await deleteContact(target);
        }}
      />

      <ConfirmDialog
        open={Boolean(pendingRetract)}
        title="Retract your card?"
        lead={
          pendingRetract
            ? `${contactDisplayName(pendingRetract)} will lose your card entirely: it disappears from their contacts, along with any notes they kept with it. They are not notified, and you cannot undo this — you would have to send your card again.`
            : ''
        }
        confirmLabel="Retract my card"
        onClose={() => setPendingRetract(null)}
        onConfirm={async () => {
          const target = pendingRetract;
          setPendingRetract(null);
          if (target) await retractMyCard(target);
        }}
      />

      <ConfirmDialog
        open={pendingBulkDelete}
        title={`Remove ${checkedIds.size} contact${checkedIds.size === 1 ? '' : 's'}?`}
        lead="Your copies of these contacts and their notes will be deleted. This cannot be undone."
        confirmLabel={`Remove ${checkedIds.size}`}
        onClose={() => setPendingBulkDelete(false)}
        onConfirm={bulkDelete}
      />

      <AddPhysicalCardDialog
        open={showActivation}
        title="Add a physical business card"
        choiceLead="Order or link a premium metal Velox card for instant tap-to-share when you meet someone in person."
        linkLabel="Link a card"
        orderLabel="Order a card"
        onClose={dismissActivation}
        onLinked={() => {
          if (userData?._id) markPhysicalCardPromptSeen(userData._id);
          setShowActivation(false);
          navigate('/my-card');
        }}
      />

      <CardIncompleteDialog
        open={Boolean(cardBlockReason)}
        onClose={() => setCardBlockReason(null)}
        onEditCard={() => {
          setCardBlockReason(null);
          navigate('/my-card');
        }}
      />

      <MeetUpgradeDialog open={meetGateOpen} onClose={() => setMeetGateOpen(false)} />

      <ScheduleMeetingModal
        contact={meetingContact}
        isOpen={Boolean(meetingContact)}
        onClose={() => setMeetingContact(null)}
        onSuccess={() => setMeetingContact(null)}
      />
    </div>
  );
};

export default ContactsPage;
